import React, { useEffect, useState } from "react";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import ChartInfoTooltip from "components/Tooltip/ChartInfoTooltip";
import PropTypes from "prop-types";
import axiosInstance from "services/httpInterceptor";
import { chartTooltips } from "components/Tooltip/Definitions/data";

import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";

// Design tokens
import { GRADIENTS } from "utils/designTokens";
import GlassCard from "components/GlassCard";

function EffectiveHashtags({ companyId, limit = 5 }) {

  const [effectiveHashtags, setEffectiveHashtags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEffectiveHashtags = async () => {
      if (!companyId) {
        return;
      }

      setError(null);

      try {
        const params = { companyId, limit };
        if (limit === 2) delete params.limit;

        const response = await axiosInstance.get(
          `/v1/frontend/publishing/effective-hashtags`, { params },
        );

        const newData = response.data || [];
        setEffectiveHashtags(currentHashtags =>
          JSON.stringify(currentHashtags) !== JSON.stringify(newData) ? newData : currentHashtags,
        );
        setLoading(false);
      } catch (err) {
        console.error("Error fetching effective hashtags:", err);
        setError("Failed to load effective hashtags.");
        setEffectiveHashtags([]);
        setLoading(false);
      }
    };

    fetchEffectiveHashtags();
  }, [companyId, limit]);

  return (
    <GlassCard sx={{ height: "100%" }}>
      <ChartInfoTooltip
        title="Most Effective Hashtags"
        tooltipText={chartTooltips.effectiveHashtags}
      >
        {loading ? (
          <StandardLoadingIndicator containerHeight="190px" />
        ) : error ? (
          <StandardMessageDisplay
            type="error"
            containerHeight="190px"
          />
        ) : effectiveHashtags.length === 0 ? (
          <StandardMessageDisplay
            type="noData"
            title="No Effective Hashtags"
            message="No specific effective hashtags were identified in the market during the chosen timeframe."
            containerHeight="190px"
          />
        ) : (
          <VuiBox sx={{
            overflowY: "auto",
            maxHeight: "200px",
            minHeight: "190px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 1.5,
          }}>
            {effectiveHashtags.map((item, index) => (
              <VuiBox
                key={index}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={1.5}
                sx={{
                  background: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "10px",
                  transform: "translateY(0)",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-3px)",
                  },
                }}
              >
                <VuiTypography variant="button" color="white" fontWeight="medium" sx={{ fontSize: "12px" }}>
                  {item.hashtag}
                </VuiTypography>
                <VuiBox
                  sx={{
                    background: GRADIENTS.engagementBadge,
                    borderRadius: "5px",
                    px: 1,
                    py: 0.5,
                    minWidth: "100px",
                    textAlign: "center",
                  }}
                >
                  <VuiTypography variant="caption" color="white" fontWeight="bold" sx={{ fontSize: "12px" }}>
                    {item.engagementRate}% Engagement
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            ))}
          </VuiBox>
        )}
      </ChartInfoTooltip>
    </GlassCard>
  );
}

EffectiveHashtags.propTypes = {
  companyId: PropTypes.string.isRequired,
  limit: PropTypes.number,
};

export default EffectiveHashtags;